import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { poiApi } from '../api/poi.js'

/**
 * POI管理ストア
 *
 * TASK-FE-105: POI管理画面実装
 */
export const usePoiStore = defineStore('poi', () => {
  // State
  const pois = ref([])
  const loading = ref(false)
  const error = ref(null)
  const total = ref(0)
  const limit = ref(20)
  const offset = ref(0)

  // 検索フィルター
  const selectedDatasetId = ref(null)
  const keyword = ref('')
  const latitude = ref(null)
  const longitude = ref(null)
  const radius = ref(null)

  // Getters
  const poisCount = computed(() => pois.value.length)

  const hasMore = computed(() => offset.value + pois.value.length < total.value)

  const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1)

  const totalPages = computed(() => Math.ceil(total.value / limit.value))

  /**
   * POI検索
   * @param {Object} params - 検索パラメータ
   */
  const searchPois = async (params = {}) => {
    loading.value = true
    error.value = null

    try {
      // デフォルトパラメータとマージ
      const searchParams = {
        dataset_id: selectedDatasetId.value ? [selectedDatasetId.value] : undefined,
        keyword: keyword.value || undefined,
        latitude: latitude.value || undefined,
        longitude: longitude.value || undefined,
        radius: radius.value || undefined,
        limit: limit.value,
        offset: offset.value,
        ...params
      }

      // 未定義の値を削除
      Object.keys(searchParams).forEach(key => {
        if (searchParams[key] === undefined || searchParams[key] === null || searchParams[key] === '') {
          delete searchParams[key]
        }
      })

      const response = await poiApi.search(searchParams)

      if (response.success) {
        pois.value = response.data?.pois || []
        total.value = response.data?.total || 0
        console.log('[PoiStore] Searched POIs:', pois.value.length, '/', total.value)
        return { success: true, data: pois.value }
      } else {
        throw new Error(response.error || 'POIの検索に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error searching POIs:', err)
      error.value = err.response?.data?.error || err.message || 'POIの検索に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * POI詳細取得
   * @param {string} poiId - POI ID
   */
  const fetchPoi = async (datasetId, poiId) => {
    loading.value = true
    error.value = null

    try {
      const response = await poiApi.get(datasetId, poiId)

      if (response.success) {
        const poi = response.data?.poi
        console.log('[PoiStore] Fetched POI:', poi?.poi_id)

        // ローカルステートを更新
        const index = pois.value.findIndex(p => p.poi_id === poi.poi_id)
        if (index !== -1) {
          pois.value[index] = poi
        } else {
          pois.value.push(poi)
        }

        return { success: true, data: poi }
      } else {
        throw new Error(response.error || 'POIの取得に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error fetching POI:', err)
      error.value = err.response?.data?.error || err.message || 'POIの取得に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * POI作成
   * @param {Object} data - POI データ
   */
  const createPoi = async (data) => {
    loading.value = true
    error.value = null

    try {
      const response = await poiApi.create(data)

      if (response.success) {
        const poi = response.data?.poi
        console.log('[PoiStore] POI created:', {
          poi_id: poi?.poi_id,
          name: poi?.name
        })

        // 検索結果を再取得
        await searchPois()

        return {
          success: true,
          data: poi
        }
      } else {
        throw new Error(response.error || 'POIの作成に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error creating POI:', err)
      error.value = err.response?.data?.error || err.response?.data?.errors || err.message || 'POIの作成に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * POI更新
   * @param {string} poiId - POI ID
   * @param {Object} data - POI データ
   */
  const updatePoi = async (datasetId, poiId, data) => {
    loading.value = true
    error.value = null

    try {
      const response = await poiApi.update(datasetId, poiId, data)

      if (response.success) {
        const poi = response.data?.poi
        console.log('[PoiStore] POI updated:', {
          poi_id: poi?.poi_id,
          name: poi?.name
        })

        // ローカルステートを更新
        const index = pois.value.findIndex(p => p.poi_id === poiId)
        if (index !== -1) {
          pois.value[index] = poi
        }

        return {
          success: true,
          data: poi
        }
      } else {
        throw new Error(response.error || 'POIの更新に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error updating POI:', err)
      error.value = err.response?.data?.error || err.response?.data?.errors || err.message || 'POIの更新に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * POI削除
   * @param {string} poiId - POI ID
   */
  const deletePoi = async (poiId) => {
    loading.value = true
    error.value = null

    try {
      const response = await poiApi.delete(poiId)

      if (response.success) {
        console.log('[PoiStore] POI deleted:', poiId)

        // ローカルステートから削除
        pois.value = pois.value.filter(p => p.poi_id !== poiId)
        total.value = Math.max(0, total.value - 1)

        return { success: true, message: response.data?.message }
      } else {
        throw new Error(response.error || 'POIの削除に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error deleting POI:', err)
      error.value = err.response?.data?.error || err.message || 'POIの削除に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * POI移動（別データセットへ）
   * @param {string} poiId - POI ID
   * @param {string} targetDatasetId - 移動先データセットID
   */
  const movePoi = async (poiId, targetDatasetId) => {
    loading.value = true
    error.value = null

    try {
      const response = await poiApi.move(poiId, { target_dataset_id: targetDatasetId })

      if (response.success) {
        console.log('[PoiStore] POI moved:', poiId, '->', targetDatasetId)

        // ローカルステートから削除（別バケットに移動したため）
        pois.value = pois.value.filter(p => p.poi_id !== poiId)
        total.value = Math.max(0, total.value - 1)

        return { success: true, message: response.data?.message }
      } else {
        throw new Error(response.error || 'POIの移動に失敗しました')
      }
    } catch (err) {
      console.error('[PoiStore] Error moving POI:', err)
      error.value = err.response?.data?.error || err.message || 'POIの移動に失敗しました'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * データセットを選択
   * @param {string} datasetId - データセットID
   */
  const selectDataset = (datasetId) => {
    selectedDatasetId.value = datasetId
    offset.value = 0
    searchPois()
  }

  /**
   * キーワードを設定
   * @param {string} kw - キーワード
   */
  const setKeyword = (kw) => {
    keyword.value = kw
    offset.value = 0
  }

  /**
   * 位置情報を設定
   * @param {number} lat - 緯度
   * @param {number} lng - 経度
   * @param {number} rad - 半径（メートル）
   */
  const setLocation = (lat, lng, rad) => {
    latitude.value = lat
    longitude.value = lng
    radius.value = rad
    offset.value = 0
  }

  /**
   * ページネーション: 次のページ
   */
  const nextPage = async () => {
    if (hasMore.value) {
      offset.value += limit.value
      await searchPois()
    }
  }

  /**
   * ページネーション: 前のページ
   */
  const prevPage = async () => {
    if (offset.value > 0) {
      offset.value = Math.max(0, offset.value - limit.value)
      await searchPois()
    }
  }

  /**
   * ページネーション: 特定ページへ移動
   * @param {number} page - ページ番号（1から開始）
   */
  const goToPage = async (page) => {
    const newOffset = (page - 1) * limit.value
    if (newOffset >= 0 && newOffset < total.value) {
      offset.value = newOffset
      await searchPois()
    }
  }

  /**
   * フィルターをクリア
   */
  const clearFilters = () => {
    keyword.value = ''
    latitude.value = null
    longitude.value = null
    radius.value = null
    offset.value = 0
  }

  /**
   * エラーをクリア
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * ストアをリセット
   */
  const reset = () => {
    pois.value = []
    loading.value = false
    error.value = null
    total.value = 0
    offset.value = 0
    selectedDatasetId.value = null
    keyword.value = ''
    latitude.value = null
    longitude.value = null
    radius.value = null
  }

  return {
    // State
    pois,
    loading,
    error,
    total,
    limit,
    offset,
    selectedDatasetId,
    keyword,
    latitude,
    longitude,
    radius,

    // Getters
    poisCount,
    hasMore,
    currentPage,
    totalPages,

    // Actions
    searchPois,
    fetchPoi,
    createPoi,
    updatePoi,
    deletePoi,
    movePoi,
    selectDataset,
    setKeyword,
    setLocation,
    nextPage,
    prevPage,
    goToPage,
    clearFilters,
    clearError,
    reset
  }
})

export default usePoiStore
